import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InfoIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import { formatCurrency, splitCategoryEmoji } from '@budget/core';
import type { CategoryRow, Tri } from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { useScope } from '../hooks/useScope';
import type { Scope } from '../components/ScopeToggle';
import { PersonBreakdown } from '../components/PersonBreakdown';
import { Tooltip } from '../components/Tooltip';
import { CardHeader } from '../components/CardHeader';
import { NetWorthHistoryChart } from '../components/NetWorthHistoryChart';
import { getCategoryIcon } from '../components/CategoryIcon';
import { getPersonName } from '../config/personNames';
import { AssetsPage } from './AssetsPage';
import { DebtsPage } from './DebtsPage';
import { CreditTab } from '../components/CreditTab';

type Tab = 'overview' | 'assets' | 'debts' | 'credit';

function fmt(n: number) {
  return formatCurrency(n);
}

function parseTab(raw: string | null): Tab {
  if (raw === 'assets' || raw === 'debts' || raw === 'credit') return raw;
  return 'overview';
}

function NetWorthCategoryCard({
  row,
  emojiByName,
  scope,
  sideTotalScoped,
  isLiability,
  onSelect,
}: {
  row: CategoryRow;
  emojiByName: Map<string, string>;
  scope: Scope;
  sideTotalScoped: number;
  /** Section context. Drives the aria-label and lets the parent group
   *  cards as Assets vs Debts. Amounts render as positive magnitudes
   *  either way — the section header is the sign context. */
  isLiability?: boolean;
  onSelect: (bareName: string) => void;
}) {
  const { emoji: inlineEmoji, name: bareName } = splitCategoryEmoji(row.name);
  const emoji = inlineEmoji ?? emojiByName.get(bareName.toLowerCase()) ?? null;
  const Icon = emoji ? null : getCategoryIcon(row.name);
  const scopedRaw = Math.abs(row[scope]);
  const share = sideTotalScoped > 0 ? (scopedRaw / sideTotalScoped) * 100 : 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(bareName)}
      className="card flex flex-col gap-4 text-left w-full cursor-pointer hover:bg-overlay transition-colors"
      aria-label={`Open ${bareName} in ${isLiability ? 'Debts' : 'Assets'} tab`}
    >
      <div className="flex items-center gap-3">
        {emoji ? (
          <span aria-hidden className="text-2xl leading-none flex-none">{emoji}</span>
        ) : Icon ? (
          <Icon size={22} weight="duotone" className="text-fg-secondary flex-none" />
        ) : null}
        <div className="font-semibold truncate flex-1">{bareName}</div>
      </div>

      <div className="flex items-baseline justify-between gap-3">
        <span className="tabular text-2xl font-semibold text-fg leading-none">
          {fmt(scopedRaw)}
        </span>
        <span className="text-xs text-fg-tertiary tabular flex-none">
          {share.toFixed(1)}%
        </span>
      </div>

      {scope === 'household' && (
        <PersonBreakdown
          dakota={Math.abs(row.dakota)}
          natalia={Math.abs(row.natalia)}
          digits={0}
        />
      )}
    </button>
  );
}

function CategorySection({
  title,
  sideTotal,
  sideTotalScoped,
  rows,
  emojiByName,
  scope,
  isLiability,
  onSelect,
}: {
  title: string;
  sideTotal: Tri;
  sideTotalScoped: number;
  rows: CategoryRow[];
  emojiByName: Map<string, string>;
  scope: Scope;
  isLiability?: boolean;
  onSelect: (bareName: string) => void;
}) {
  const visible = useMemo(
    () => rows.filter((r) => r[scope] !== 0).sort((a, b) => Math.abs(b[scope]) - Math.abs(a[scope])),
    [rows, scope],
  );
  if (visible.length === 0) return null;
  return (
    <section className="space-y-3">
      <CardHeader
        title={title}
        right={
          <span className="tabular text-lg font-semibold text-fg">
            {fmt(Math.abs(sideTotal[scope]))}
          </span>
        }
      />
      <div className="grid grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1 gap-3">
        {visible.map((row) => (
          <NetWorthCategoryCard
            key={row.name}
            row={row}
            emojiByName={emojiByName}
            scope={scope}
            sideTotalScoped={sideTotalScoped}
            isLiability={isLiability}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  );
}

function NetWorthOverview() {
  const { data, isLoading, error } = useDashboard();
  const { scope } = useScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const openCategory = (side: 'assets' | 'debts', bareName: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', side);
    next.set('cat', bareName);
    setSearchParams(next, { replace: true });
  };

  // Asset/debt category names on the Assets and Debts pages carry an emoji
  // prefix, but the netWorth payload's category rows are plain text. Build a
  // bare-name → emoji map so the overview cards render the same icons.
  const emojiByName = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    const indexFrom = (categories: { name: string }[]) => {
      for (const c of categories) {
        const { emoji, name } = splitCategoryEmoji(c.name);
        if (emoji) map.set(name.toLowerCase(), emoji);
      }
    };
    indexFrom(data.assets.categories);
    indexFrom(data.debts.categories);
    indexFrom(data.investments.categories);
    return map;
  }, [data]);

  if (error) return <div className="alert-error">{error}</div>;
  if (!data && isLoading) {
    return <div className="card text-center py-12 text-fg-secondary">Loading…</div>;
  }
  if (!data) return null;

  const heroEyebrow =
    scope === 'household' ? 'Household net worth' : `${getPersonName(scope)} net worth`;
  const assetsScoped = data.netWorth.assetsTotal[scope];
  // Liabilities are stored as positive magnitudes; use the magnitude as the
  // denominator for per-category share %.
  const liabsScoped = Math.abs(data.netWorth.liabilitiesTotal[scope]);

  return (
    <>
      <div className="card space-y-5">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="hero-eyebrow flex items-center gap-1.5">
              <span>{heroEyebrow}</span>
              <Tooltip
                wide
                content="Projection adds your scheduled retirement contributions, non-retirement savings, and debt paydown to today's balance, compounding at an 8% annual return."
              >
                <InfoIcon
                  size={14}
                  weight="regular"
                  className="text-fg-tertiary cursor-help"
                  tabIndex={0}
                  aria-label="About the net-worth projection"
                />
              </Tooltip>
            </span>
            <span className="hero-balance tabular leading-none">
              {fmt(data.netWorth.netWorth[scope])}
            </span>
          </div>
          <PersonBreakdown
            dakota={data.netWorth.netWorth.dakota}
            natalia={data.netWorth.netWorth.natalia}
            digits={0}
          />
        </div>
        <NetWorthHistoryChart
          flush
          scope={scope}
          currentNetWorth={data.netWorth.netWorth}
          retirement={data.retirement}
          monthlyDebtPaydown={data.debts.totals.minimumPrincipal}
          monthlySavings={data.budget.summary.monthlySavings}
        />
      </div>

      <CategorySection
        title="Assets"
        sideTotal={data.netWorth.assetsTotal}
        sideTotalScoped={assetsScoped}
        rows={data.netWorth.assets}
        emojiByName={emojiByName}
        scope={scope}
        onSelect={(name) => openCategory('assets', name)}
      />
      <CategorySection
        title="Liabilities"
        sideTotal={data.netWorth.liabilitiesTotal}
        sideTotalScoped={liabsScoped}
        rows={data.netWorth.liabilities}
        emojiByName={emojiByName}
        scope={scope}
        isLiability
        onSelect={(name) => openCategory('debts', name)}
      />
    </>
  );
}

export function NetWorthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseTab(searchParams.get('tab'));
  const setView = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const title =
    view === 'overview'
      ? 'Net Worth'
      : view === 'assets'
        ? 'Assets'
        : view === 'debts'
          ? 'Debts'
          : 'Credit';
  const subtitle =
    view === 'overview'
      ? 'Assets minus liabilities, broken down by category'
      : view === 'assets'
        ? "Where your money lives and what it's earning"
        : view === 'debts'
          ? "What you owe and what it's costing each month"
          : 'Track your credit scores over time';

  return (
    <div className="page-container space-y-5">
      <header>
        <div className="page-header">
          <div>
            <h1 className="page-title">{title}</h1>
            <p className="page-subtitle">{subtitle}</p>
          </div>
        </div>
      </header>
      <div className="tab-row max-md:tab-row-sticky max-md:-mt-5">
        <div className="page-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={view === 'overview'}
            onClick={() => setView('overview')}
            className={clsx('page-tab', view === 'overview' && 'page-tab-active')}
          >
            Net Worth
          </button>
          <button
            role="tab"
            aria-selected={view === 'assets'}
            onClick={() => setView('assets')}
            className={clsx('page-tab', view === 'assets' && 'page-tab-active')}
          >
            Assets
          </button>
          <button
            role="tab"
            aria-selected={view === 'debts'}
            onClick={() => setView('debts')}
            className={clsx('page-tab', view === 'debts' && 'page-tab-active')}
          >
            Debts
          </button>
          <button
            role="tab"
            aria-selected={view === 'credit'}
            onClick={() => setView('credit')}
            className={clsx('page-tab', view === 'credit' && 'page-tab-active')}
          >
            Credit
          </button>
        </div>
      </div>

      {view === 'overview' && <NetWorthOverview />}
      {view === 'assets' && <AssetsPage embedded />}
      {view === 'debts' && <DebtsPage embedded />}
      {view === 'credit' && <CreditTab />}
    </div>
  );
}
